import React, { useEffect, useMemo, useState } from 'react';
import { useFormik, getIn } from 'formik';
import * as Yup from 'yup';
import { MdClose } from 'react-icons/md';
import { useAuth } from '../../Context/AuthContext';
import { getAllProviderSettings } from '../../Services/ProviderService';
import { toast } from 'react-toastify';
import SearchableDropdown from '../Subscription/SearchableDropdown';
import DocumentUpload from '../Common/DocumentUpload';
import CustomDatePicker from '../Common/CustomDatePicker';

const emptyForm = {
  id: null,
  provider_id: null,
  status_id: null,
  file_id: null,
  board_name: '',
  is_primary_board: '',
  is_board_eligible: '',
  certifications: '',
  cert_status: '',
  cert_type: '',
  exam_passed: '',
  speciality_exam_passed: '',
  no_of_speciality_exam_attempts: '',
  exam_date: null,
  certificate_duration: '',
  issue_date: null,
  expiration_date: null,
  recertification_date: null,
  moc_occ_status: '',
  meeting_moc_occ: '',
  type: 'create',
  document: null,
};

const validationSchema = Yup.object({
  certifications: Yup.string().trim().required('Certification is required'),
  board_name: Yup.string().trim().nullable(),
  is_primary_board: Yup.string().oneOf(['true', 'false', ''], 'Select an option').nullable(),
  is_board_eligible: Yup.string().oneOf(['true', 'false', ''], 'Select an option').nullable(),
  issue_date: Yup.date().nullable().typeError('Invalid date'),
  expiration_date: Yup.date().nullable().typeError('Invalid date'),
  recertification_date: Yup.date().nullable().typeError('Invalid date'),
  exam_date: Yup.date().nullable().typeError('Invalid date'),
  no_of_speciality_exam_attempts: Yup.number()
    .nullable()
    .transform((value, originalValue) => (originalValue === '' ? null : Number(originalValue)))
    .min(0, 'Must be 0 or greater'),
  certificate_duration: Yup.number()
    .nullable()
    .transform((value, originalValue) => (originalValue === '' ? null : Number(originalValue)))
    .min(0, 'Must be 0 or greater'),
  cert_status: Yup.string().trim().nullable(),
  cert_type: Yup.string().trim().nullable(),
  moc_occ_status: Yup.string().trim().nullable(),
  meeting_moc_occ: Yup.string().trim().nullable(),
})
  .test(
    'board-required',
    'Either Primary board or Board eligible must be true',
    function (values) {
      const { sectionType } = this.options.context || {};
      if (sectionType === 'additional') return true;
      return values.is_primary_board === 'true' || values.is_board_eligible === 'true';
    }
  );

const CERTIFICATION_OPTIONS = [
  { id: '', label: 'Select certificate' },
  { id: 'Board Certification', label: 'Board Certification' },
  { id: 'Professional Organization', label: 'Professional Organization' },
  { id: 'Additional Certificate', label: 'Additional Certificate' },
];

const CERTIFICATE_STATUS_SETTING_ID = 58;
const ORGANIZATION_NAME_SETTING_ID = 59;

const mapProviderSettingItem = (item) => ({
  value: item.id ?? item.Id ?? item.value ?? item.value_id ?? null,
  label: item.name ?? item.Name ?? item.code ?? String(item.id ?? ''),
});

function getFieldError(fieldName, formik) {
  const touched = getIn(formik.touched, fieldName);
  const error = getIn(formik.errors, fieldName);
  return touched && error ? error : null;
}

function stringifyBoolean(value) {
  if (value === true || value === 'true') return 'true';
  if (value === false || value === 'false') return 'false';
  return '';
}

function normalizeBooleanValue(value) {
  if (value === 'true') return true;
  if (value === 'false') return false;
  return null;
}

function normalizeValues(values) {
  return {
    ...values,
    is_primary_board: normalizeBooleanValue(values.is_primary_board),
    is_board_eligible: normalizeBooleanValue(values.is_board_eligible),
    exam_passed: normalizeBooleanValue(values.exam_passed),
    speciality_exam_passed: normalizeBooleanValue(values.speciality_exam_passed),
    no_of_speciality_exam_attempts: values.no_of_speciality_exam_attempts
      ? Number(values.no_of_speciality_exam_attempts)
      : null,
    certificate_duration: values.certificate_duration
      ? Number(values.certificate_duration)
      : null,
    cert_status: values.cert_status !== undefined && values.cert_status !== '' ? Number(values.cert_status) : null,
    cert_type: values.cert_type !== undefined && values.cert_type !== '' ? Number(values.cert_type) : null,
    moc_occ_status: values.moc_occ_status ? Number(values.moc_occ_status) : null,
    meeting_moc_occ: normalizeBooleanValue(values.meeting_moc_occ),
    issue_date: values.issue_date || null,
    expiration_date: values.expiration_date || null,
    recertification_date: values.recertification_date || null,
    exam_date: values.exam_date || null,
    type: values.type || 'create',
  };
}

export default function CertificateModal({
  isOpen,
  onClose,
  onSave,
  initialData,
  sectionType = 'board',
  providerId,
  isLoading,
}) {
  const { token } = useAuth();
  const [providerSettings, setProviderSettings] = useState({
    cert_status: [],
    board_name: [],
    moc_occ_status: [],
  });
  const [settingsLoading, setSettingsLoading] = useState(false);

  const loadProviderSettings = async () => {
    if (!token) return;
    try {
      setSettingsLoading(true);
      const entries = [
        ['cert_status', CERTIFICATE_STATUS_SETTING_ID],
        ['board_name', ORGANIZATION_NAME_SETTING_ID],
        ['moc_occ_status', 57],
      ];
      const results = await Promise.all(
        entries.map(async ([key, typeId]) => {
          try {
            const response = await getAllProviderSettings(token, typeId);
            if (response?.is_success && Array.isArray(response.data)) {
              return { key, options: response.data.map(mapProviderSettingItem) };
            } else if (response?.is_success === false && response?.status_code === 500) {
              toast.error('Internal Server Error. Please Contact Support Team.');
            } else {
              toast.error(response?.message || `Failed to load settings`);
            }
          } catch (error) {
            console.error(`Failed to load provider settings for ${key}:`, error);
          }
          return { key, options: [] };
        })
      );
      setProviderSettings(results.reduce((acc, item) => ({ ...acc, [item.key]: item.options }), {}));
    } finally {
      setSettingsLoading(false);
    }
  };

  useEffect(() => {
    loadProviderSettings();
  }, [token]);

  const initialValues = useMemo(
    () => ({
      ...emptyForm,
      ...initialData,
      provider_id: providerId ?? initialData?.provider_id ?? null,
      type: initialData?.id ? 'update' : 'create',
      document: initialData?.document ?? initialData?.file_name ?? initialData?.fileName ?? null,
      is_primary_board: stringifyBoolean(initialData?.is_primary_board),
      is_board_eligible: stringifyBoolean(initialData?.is_board_eligible),
      exam_passed: stringifyBoolean(initialData?.exam_passed),
      speciality_exam_passed: stringifyBoolean(initialData?.speciality_exam_passed),
      no_of_speciality_exam_attempts:
        initialData?.no_of_speciality_exam_attempts ?? '',
      certificate_duration: initialData?.certificate_duration ?? '',
      cert_status: initialData?.cert_status ?? '',
      cert_type: initialData?.cert_type ?? '',
      moc_occ_status: initialData?.moc_occ_status ?? '',
      meeting_moc_occ: initialData?.meeting_moc_occ ?? '',
    }),
    [initialData, providerId]
  );

  const formik = useFormik({
    initialValues,
    enableReinitialize: true,
    validationSchema,
    validateOnBlur: true,
    validateOnChange: false,
    validateOnMount: false,
    onSubmit: (values) => {
      onSave(normalizeValues(values));
    },
    context: { sectionType },
  });

  const handlePrimaryBoardChange = (event) => {
    const value = event.target.value;
    formik.setFieldValue('is_primary_board', value);
    if (value === 'true') {
      formik.setFieldValue('is_board_eligible', '');
    }
  };

  const handleBoardEligibleChange = (event) => {
    const value = event.target.value;
    formik.setFieldValue('is_board_eligible', value);
    if (value === 'true') {
      formik.setFieldValue('is_primary_board', '');
    }
  };

  const showBoardFields =
    sectionType !== 'additional' &&
    formik.values.is_board_eligible === 'true';

  if (!isOpen) return null;

  const sectionLabel =
    sectionType === 'board'
      ? 'Board Certification'
      : sectionType === 'organization'
      ? 'Professional Organization'
      : 'Additional Certificate';

  return (
    <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(var(--hc-black-rgb), 0.5)' }}>
      <div className="modal-dialog modal-xl modal-dialog-centered ">
        <div className="modal-content border-0 rounded-4">
          <div className="modal-header border-bottom-0 px-4 py-2 d-flex justify-content-between align-items-center">
            <h5 className="modal-title fw-bold hc-text-primary m-0">
              {initialData?.id ? `Edit ${sectionLabel}` : `Add ${sectionLabel}`}
            </h5>
            <button type="button" className="btn-close" onClick={onClose} disabled={isLoading} />
          </div>

          <form onSubmit={formik.handleSubmit}>
            <div className="modal-body px-4 py-2">
              <div className="mb-4 p-4 rounded-4 border bg-light-subtle">
                <h6 className="fw-semibold hc-text-primary mb-3">Basic Details</h6>
                <div className="row g-3">
                  <div className="col-lg-6">
                    <label className="form-label fw-semibold">Certification</label>
                    <input
                      {...formik.getFieldProps('certifications')}
                      className={`form-control rounded-3 shadow-sm ${getFieldError('certifications', formik) ? 'is-invalid' : ''}`}
                      placeholder="Enter certification"
                    />
                    <div className="invalid-feedback">{getFieldError('certifications', formik)}</div>
                  </div>

                  {sectionType !== 'additional' && (
                    <div className="col-lg-6">
                      <SearchableDropdown
                        label={sectionType === 'additional' ? 'Certification #' : 'Board / Organization Name'}
                        placeholder={sectionType === 'additional' ? 'Select certification number' : 'Select board or organization name'}
                        options={providerSettings.board_name}
                        value={formik.values.board_name ?? ''}
                        onChange={(value) => formik.setFieldValue('board_name', value ?? '')}
                        disabled={settingsLoading && !providerSettings.board_name.length}
                        loading={settingsLoading && !providerSettings.board_name.length}
                        emptyMessage="No board or organization names found"
                        allowCustom={true}
                      />
                    </div>
                  )}
                </div>
              </div>

              {sectionType !== 'additional' && (
                <div className="mb-4 p-4 rounded-4 border bg-light-subtle">
                  <h6 className="fw-semibold hc-text-primary mb-3">Board Eligibility</h6>
                  <div className="row g-3">
                    <div className="col-md-6">
                      <label className="form-label fw-semibold">Primary Board</label>
                      <select
                        name="is_primary_board"
                        value={formik.values.is_primary_board}
                        onChange={handlePrimaryBoardChange}
                        className={`form-select rounded-3 shadow-sm ${getFieldError('is_primary_board', formik) || getFieldError('is_board_eligible', formik) ? 'is-invalid' : ''}`}
                      >
                        <option value="">Select Option</option>
                        <option value="true">True</option>
                        <option value="false">False</option>
                      </select>
                    </div>
                    <div className="col-md-6">
                      <label className="form-label fw-semibold">Board Eligible</label>
                      <select
                        name="is_board_eligible"
                        value={formik.values.is_board_eligible}
                        onChange={handleBoardEligibleChange}
                        className={`form-select rounded-3 shadow-sm ${getFieldError('is_primary_board', formik) || getFieldError('is_board_eligible', formik) ? 'is-invalid' : ''}`}
                      >
                        <option value="">Select Option</option>
                        <option value="true">True</option>
                        <option value="false">False</option>
                      </select>
                    </div>
                    <div className="col-12">
                      <div className="invalid-feedback d-block">
                        {getFieldError('is_primary_board', formik) || getFieldError('is_board_eligible', formik)}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {showBoardFields && (
                <div className="mb-4 p-4 rounded-4 border bg-light-subtle">
                  <h6 className="fw-semibold hc-text-primary mb-3">Board Exam Details</h6>
                  <div className="row g-3">
                    <div className="col-lg-4">
                      <label className="form-label fw-semibold">Speciality Exam Passed</label>
                      <select
                        {...formik.getFieldProps('speciality_exam_passed')}
                        className="form-select rounded-3 shadow-sm"
                      >
                        <option value="">Select Option</option>
                        <option value="true">True</option>
                        <option value="false">False</option>
                      </select>
                    </div>
                    <div className="col-lg-4">
                      <label className="form-label fw-semibold">Exam Passed</label>
                      <select
                        {...formik.getFieldProps('exam_passed')}
                        className="form-select rounded-3 shadow-sm"
                      >
                        <option value="">Select Option</option>
                        <option value="true">True</option>
                        <option value="false">False</option>
                      </select>
                    </div>
                    <div className="col-lg-4">
                      <label className="form-label fw-semibold">No. of Attempts</label>
                      <input
                        type="number"
                        {...formik.getFieldProps('no_of_speciality_exam_attempts')}
                        className="form-control rounded-3 shadow-sm"
                        min="0"
                        placeholder="Enter attempts"
                      />
                    </div>
                  </div>
                </div>
              )}

              <div className="mb-4 p-4 rounded-4 border bg-light-subtle">
                <h6 className="fw-semibold hc-text-primary mb-3">Dates & Duration</h6>
                <div className="row g-3">
                  <div className="col-lg-3 col-md-6">
                    <label className="form-label fw-semibold">Issue Date</label>
                    <CustomDatePicker
                      {...formik.getFieldProps('issue_date')}
                      formik={formik}
                    />
                    {/* <input
                      type="date"
                      {...formik.getFieldProps('issue_date')}
                      className="form-control rounded-3 shadow-sm"
                    /> */}
                  </div>
                  <div className="col-lg-3 col-md-6">
                    <label className="form-label fw-semibold">Expiration Date</label>
                    <CustomDatePicker
                      {...formik.getFieldProps('expiration_date')}
                      formik={formik}
                    />
                    {/* <input
                      type="date"
                      {...formik.getFieldProps('expiration_date')}
                      className="form-control rounded-3 shadow-sm"
                    /> */}
                  </div>
                  <div className="col-lg-3">
                    <label className="form-label fw-semibold">Recertification Date</label>

                      <CustomDatePicker
                        {...formik.getFieldProps('recertification_date')}
                        formik={formik}
                      />
                  </div>
                  <div className="col-lg-3">
                    <SearchableDropdown
                      label="Certificate Status"
                      placeholder="Select Option"
                      options={providerSettings.cert_status}
                      value={formik.values.cert_status ?? ''}
                      onChange={(value) => formik.setFieldValue('cert_status', value ?? '')}
                      disabled={settingsLoading && !providerSettings.cert_status.length}
                      loading={settingsLoading && !providerSettings.cert_status.length}
                      emptyMessage="No certificate statuses found"
                    />
                  </div>

                  <div className="col-lg-3">
                    <label className="form-label fw-semibold">Duration (months)</label>
                    <input
                      type="number"
                      {...formik.getFieldProps('certificate_duration')}
                      className="form-control rounded-3 shadow-sm"
                      min="0"
                      placeholder="Enter duration"
                    />
                  </div>
                </div>
              </div>

              <div className="mb-4 p-4 rounded-4 border bg-light-subtle">
                <h6 className="fw-semibold hc-text-primary mb-3">MOC Details</h6>
                <div className="row g-3">
                  <div className="col-lg-6">
                        <SearchableDropdown
                          label="MOC OCC Status"
                          placeholder="Select Option"
                          options={providerSettings.moc_occ_status}
                          value={formik.values.moc_occ_status ?? ''}
                          onChange={(value) => formik.setFieldValue('moc_occ_status', value ?? '')}
                          disabled={settingsLoading && !providerSettings.moc_occ_status.length}
                          loading={settingsLoading && !providerSettings.moc_occ_status.length}
                          emptyMessage="No MOC OCC statuses found"
                        />
                  </div>
                  <div className="col-lg-6">
                    <label className="form-label fw-semibold">Meeting MOC OCC</label>
                    <select
                      name="meeting_moc_occ"
                      value={formik.values.meeting_moc_occ}
                      onChange={formik.handleChange}
                      className="form-select rounded-3 shadow-sm"
                    >
                      <option value="">Select Option</option>
                      <option value="true">Yes</option>
                      <option value="false">No</option>
                    </select>
                  </div>
                </div>
              </div>

              <div className="mb-4 p-4 rounded-4 border bg-light-subtle">
                <h6 className="fw-semibold hc-text-primary mb-3">Document</h6>
                <DocumentUpload
                  id="certificate-document-upload"
                  label="Upload Document"
                  value={formik.values.document}
                  onChange={(file) => formik.setFieldValue('document', file)}
                />
              </div>
            </div>

            <div className="modal-footer border-top-0 p-4 d-flex justify-content-end gap-2">
              <button type="button" className="btn btn-light rounded-3 px-4" onClick={onClose} disabled={isLoading}>
                Cancel
              </button>
              <button type="submit" className="btn hc-bg-primary text-white rounded-3 px-4 d-flex align-items-center justify-content-center gap-2" disabled={isLoading} aria-busy={isLoading}>
                {isLoading ? (
                  <>
                    <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                    Saving...
                  </>
                ) : (
                  'Save'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
